import { useParams, Link } from 'react-router-dom';
import { categories } from '@/mocks/categories';
import { articles } from '@/mocks/articles';
import Breadcrumb, { getCategoryBreadcrumb } from '@/components/feature/Breadcrumb';
import ArticleCard from '@/components/base/ArticleCard';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const category = categories.find((c) => c.slug === slug);

  const filteredArticles = articles
    .filter((a) => a.category === slug)
    .sort((a, b) => new Date(b.publishDate).getTime() - new Date(a.publishDate).getTime());

  const breadcrumbItems = getCategoryBreadcrumb(slug || '');

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: category ? `${category.name} - 最新发布页` : '分类页面',
    description: category?.description || '',
    url: `https://gdbtui7.cn/category/${slug}`,
  };

  if (!category) {
    return (
      <main className="min-h-screen pt-20 pb-14 px-4 md:px-6">
        <div className="mx-auto max-w-6xl text-center py-20">
          <h2 className="text-xl font-heading font-semibold text-foreground-700 mb-3">分类未找到</h2>
          <Link to="/" className="text-sm text-primary-600 hover:text-primary-700 cursor-pointer">
            返回首页
          </Link>
        </div>
      </main>
    );
  }

  return (
    <>
      <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      <main className="min-h-screen pt-20 pb-14">
        <div className="mx-auto max-w-6xl px-4 md:px-6">
          <div className="mb-6">
            <Breadcrumb items={breadcrumbItems} />
          </div>

          <div className="mb-8">
            <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 mb-2">
              {category.name}
            </h1>
            <p className="text-sm md:text-base text-foreground-600">{category.description}</p>
          </div>

          <div className="flex items-center gap-2 mb-8 overflow-x-auto pb-2">
            <Link
              to="/category/latest-news"
              className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-all duration-200 cursor-pointer ${
                slug === 'latest-news'
                  ? 'bg-primary-500 text-background-50 dark:text-foreground-950'
                  : 'bg-background-100 text-foreground-700 hover:bg-background-200'
              }`}
            >
              最新资讯
            </Link>
            <Link
              to="/category/product-releases"
              className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-all duration-200 cursor-pointer ${
                slug === 'product-releases'
                  ? 'bg-primary-500 text-background-50 dark:text-foreground-950'
                  : 'bg-background-100 text-foreground-700 hover:bg-background-200'
              }`}
            >
              产品发布
            </Link>
            <Link
              to="/category/announcements"
              className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-all duration-200 cursor-pointer ${
                slug === 'announcements'
                  ? 'bg-primary-500 text-background-50 dark:text-foreground-950'
                  : 'bg-background-100 text-foreground-700 hover:bg-background-200'
              }`}
            >
              平台公告
            </Link>
            <Link
              to="/category/events"
              className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-all duration-200 cursor-pointer ${
                slug === 'events'
                  ? 'bg-primary-500 text-background-50 dark:text-foreground-950'
                  : 'bg-background-100 text-foreground-700 hover:bg-background-200'
              }`}
            >
              活动信息
            </Link>
          </div>

          {filteredArticles.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6" data-product-shop="">
              {filteredArticles.map((article) => (
                <ArticleCard
                  key={article.id}
                  {...article}
                  categoryName={category.name}
                />
              ))}
            </div>
          ) : (
            <div className="text-center py-16">
              <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-100 text-foreground-400">
                <i className="ri-inbox-line text-2xl"></i>
              </div>
              <p className="text-sm text-foreground-600">该分类暂无内容</p>
            </div>
          )}
        </div>
      </main>
    </>
  );
}